import React, { useEffect, useMemo, useReducer } from 'react'

import { appContextReducer, initialAppContext } from '../utils'
import { getPersistedAppContext, persistAppContext } from '../utils/app-context-storage'
import { TAppContextProvider } from '../types'
import { AppContext } from './AppContext'

/**
 * Provider responsible for setting:
 * - isAppActive: defines if the app is active or not by country
 * - isAppLoading: defines if the app data is being loaded or not by country
 * - isAppError: defines if fetching the app data returned an error or not by country
 * - country: BR, US, AR, etc
 * - userType: PF, PJ, MEI, US, AR, etc
 * - accountType: DA (default account), GA (global account), etc
 * - language: pt-BR, en-US, es-ES, etc
 * - components: json with components text content by country and language,
 * - endpoints: json with endpoints by country,
 *
 * @param { children }: TAppContextProvider
 *
 */
export const AppContextProvider = ({ children }: TAppContextProvider) => {
  const [context, dispatch] = useReducer(appContextReducer, initialAppContext, (initialState) => ({
    ...initialState,
    ...getPersistedAppContext(),
  }))

  useEffect(() => {
    persistAppContext(context)
  }, [context])

  useEffect(() => {
    const persistCurrentContext = () => {
      persistAppContext(context)
    }

    window.addEventListener('focus', persistCurrentContext)
    window.addEventListener('pageshow', persistCurrentContext)
    document.addEventListener('visibilitychange', persistCurrentContext)

    return () => {
      window.removeEventListener('focus', persistCurrentContext)
      window.removeEventListener('pageshow', persistCurrentContext)
      document.removeEventListener('visibilitychange', persistCurrentContext)
    }
  }, [context])

  const contextValue = useMemo(
    () => ({
      ...context,
      dispatch,
    }),
    [context, dispatch],
  )

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
}
